import { t as uiText } from '../i18n';
export const DEFAULT_WORKSPACE_BASE_COLOR_KEY = 'beige' as const;

export type WorkspaceBaseColorKey =
  | 'enji'
  | 'rose'
  | 'apricot'
  | 'beige'
  | 'sand'
  | 'olive'
  | 'honey'
  | 'green'
  | 'mint'
  | 'sky'
  | 'blue'
  | 'navy'
  | 'purple'
  | 'lavenderGray'
  | 'slate';

export type WorkspaceColorTokens = {
  primary: string;
  primaryDark: string;
  bgSoft: string;
  borderSoft: string;
  textOnPrimary: string;
};

export const WORKSPACE_BASE_COLOR_PALETTES: Record<
  WorkspaceBaseColorKey,
  WorkspaceColorTokens
> = {
  enji: {
    primary: '#9a0c19',
    primaryDark: '#7e0a14',
    bgSoft: '#f0d4d7',
    borderSoft: '#f9ecee',
    textOnPrimary: '#ffffff',
  },
  rose: {
    primary: '#c16b88',
    primaryDark: '#a85673',
    bgSoft: '#f0dce5',
    borderSoft: '#f9eef3',
    textOnPrimary: '#ffffff',
  },
  apricot: {
    primary: '#E09B73',
    primaryDark: '#C98660',
    bgSoft: '#F8D9C4',
    borderSoft: '#FDF0E7',
    textOnPrimary: '#ffffff',
  },
  beige: {
    primary: '#cfae91',
    primaryDark: '#b89a7a',
    bgSoft: '#E9D8C9',
    borderSoft: '#f5f1ed',
    textOnPrimary: '#ffffff',
  },
  sand: {
    primary: '#C4B18E',
    primaryDark: '#AD9B78',
    bgSoft: '#EAE4D3',
    borderSoft: '#F5F2EA',
    textOnPrimary: '#ffffff',
  },
  olive: {
    primary: '#8FA06A',
    primaryDark: '#7A8C57',
    bgSoft: '#DCE2CC',
    borderSoft: '#EEF1E6',
    textOnPrimary: '#ffffff',
  },
  honey: {
    primary: '#d8c45a',
    primaryDark: '#c0ac44',
    bgSoft: '#f5f0d4',
    borderSoft: '#faf8eb',
    textOnPrimary: '#333333',
  },
  green: {
    primary: '#5ea67a',
    primaryDark: '#4b8c65',
    bgSoft: '#d9ecdf',
    borderSoft: '#ebf7ef',
    textOnPrimary: '#ffffff',
  },
  mint: {
    primary: '#5BBD9E',
    primaryDark: '#47A688',
    bgSoft: '#CDEFE3',
    borderSoft: '#E8F7F1',
    textOnPrimary: '#ffffff',
  },
  sky: {
    primary: '#6AAFE6',
    primaryDark: '#5199D0',
    bgSoft: '#D7EBFF',
    borderSoft: '#ECF5FF',
    textOnPrimary: '#ffffff',
  },
  blue: {
    primary: '#4f83cc',
    primaryDark: '#3b6dad',
    bgSoft: '#dbe9fb',
    borderSoft: '#edf4ff',
    textOnPrimary: '#ffffff',
  },
  navy: {
    primary: '#1c4587',
    primaryDark: '#153669',
    bgSoft: '#d4dcea',
    borderSoft: '#e9edf4',
    textOnPrimary: '#ffffff',
  },
  purple: {
    primary: '#7F65B8',
    primaryDark: '#6A52A2',
    bgSoft: '#E0D8F0',
    borderSoft: '#EDEAF8',
    textOnPrimary: '#ffffff',
  },
  lavenderGray: {
    primary: '#9990C4',
    primaryDark: '#837AAE',
    bgSoft: '#E3E0F3',
    borderSoft: '#F1F0F9',
    textOnPrimary: '#ffffff',
  },
  slate: {
    primary: '#8892A4',
    primaryDark: '#727D91',
    bgSoft: '#D9DDE5',
    borderSoft: '#ECEEF2',
    textOnPrimary: '#ffffff',
  },
};

export const WORKSPACE_BASE_COLOR_OPTIONS: Array<{
  key: WorkspaceBaseColorKey;
  label: string;
}> = [
  { key: 'enji', get label() { return uiText("Burgundy"); } },
  { key: 'rose', get label() { return uiText("Rose"); } },
  { key: 'apricot', get label() { return uiText("Apricot"); } },
  { key: 'beige', get label() { return uiText("Beige"); } },
  { key: 'sand', get label() { return uiText("Sand"); } },
  { key: 'olive', get label() { return uiText("Olive"); } },
  { key: 'honey', get label() { return uiText("Honey"); } },
  { key: 'green', get label() { return uiText("Green"); } },
  { key: 'mint', get label() { return uiText("Mint"); } },
  { key: 'sky', get label() { return uiText("Sky"); } },
  { key: 'blue', get label() { return uiText("Blue"); } },
  { key: 'navy', get label() { return uiText("Navy"); } },
  { key: 'purple', get label() { return uiText("Purple"); } },
  { key: 'lavenderGray', get label() { return uiText("Lavender gray"); } },
  { key: 'slate', get label() { return uiText("Slate"); } },
];

export const isWorkspaceBaseColorKey = (
  value: unknown
): value is WorkspaceBaseColorKey =>
  typeof value === 'string' && value in WORKSPACE_BASE_COLOR_PALETTES;

export const resolveWorkspacePalette = (
  key?: string | null
): WorkspaceColorTokens => {
  if (key && isWorkspaceBaseColorKey(key)) {
    return WORKSPACE_BASE_COLOR_PALETTES[key];
  }
  return WORKSPACE_BASE_COLOR_PALETTES[DEFAULT_WORKSPACE_BASE_COLOR_KEY];
};
